import { useEffect } from "react";
import Header from "../Header";
import PageTransition from "../../PageTransition";
import "./BlogDetails.css";

import img1 from "./intelImages/common-designs-mistake-in-branding.webp";
import img2 from "./intelImages/logo-color-selection-mistakes.webp";
import img3 from "./intelImages/how-to-avoid-costly-branding-mistakes.webp";

export default function BlogDetails3() {
  useEffect(() => {
    document.body.style.overflow = "auto";
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header visible mode="normal" />

      <PageTransition>
        <section className="blog-detail">
          <div className="blog-container container">

            {/* ================= HERO ================= */}
            <header className="blog-hero">
              <h1>The Hidden Debt: Design Mistakes That Cost You Later</h1>

              <p className="blog-subtext">
                Building a brand is like building a house. If the foundation is cracked,
                it doesn't matter how expensive the paint is—eventually, the structure
                will fail.
              </p>

              <p className="blog-subtext">
                In the rush to launch, many companies fall into common branding mistakes
                startups make that require expensive, painful rebrands just a few years
                down the line.
              </p>

              <p className="blog-subtext">
                Here is how to identify—and avoid—the branding design mistakes that hurt
                business growth.
              </p>
            </header>

            {/* ================= INTRO ================= */}
            <section className="blog-section">
              <h2>Why Branding Design Matters</h2>
              <p>
                Branding isn't just "making things look pretty." It is a visual language
                that communicates value, trust, and positioning.
              </p>
              <p>
                High-quality design tells the world you are a professional; poor design
                tells them you are a hobbyist.
              </p>
              <p>
                When you ignore the fundamentals, you aren't just making a "design
                choice"—you are creating a barrier between you and your customers.
              </p>
            </section>

            {/* ================= SPLIT 1 ================= */}
            <section className="blog-split blog-split2">
              <div className="audit-list">
                <h2>Common Design Mistakes in Branding</h2>

                <p><strong>1. Designing for Trends, Not Longevity</strong></p>
                <p>
                  One of the most frequent mistakes while creating a brand identity is
                  chasing "aesthetic of the moment."
                </p>
                <p>
                  <strong>The Risk:</strong> What looks "cool" in 2025 (like
                  ultra-minimalism or neon gradients) might look incredibly dated by
                  2027.
                </p>
                <p>
                  <strong>The Fix:</strong> Aim for timelessness. A brand should be able
                  to live for a decade without needing a total overhaul.
                </p>

                <p><strong>2. Lack of Scalability (The "Small Screen" Trap)</strong></p>
                <p>
                  Many logo design mistakes happen because a logo looks great on a giant
                  MacBook Pro screen but becomes an unrecognizable blob on a smartphone
                  or a favicon.
                </p>
                <p>
                  <strong>The Risk:</strong> If your logo has too much fine detail, it
                  loses its impact when shrunk down for social media or app icons.
                </p>
                <p>
                  <strong>The Fix:</strong> Test your logo at the size of a postage
                  stamp. If you can’t tell what it is, it’s too complex.
                </p>
              </div>

              <div className="image-box">
                <img src={img1} alt="Branding design mistakes illustration" />
              </div>
            </section>

            {/* ================= SPLIT 2 ================= */}
            <section className="blog-split">
              <div className="audit-list">
                <h2>Logo & Color Selection Mistakes</h2>

                <p><strong>3. Ignoring Color Psychology</strong></p>
                <p>
                  Color isn't just a preference; it's a feeling. Choosing a color simply
                  because it’s your favorite is a classic brand design mistake.
                </p>
                <p>
                  <strong>The Risk:</strong> Using a high-energy red for a meditation app
                  or a clinical grey for a children’s toy brand creates a subconscious
                  disconnect for the user.
                </p>
                <p>
                  <strong>The Fix:</strong> Research color theory. Ensure your palette
                  aligns with the emotional response you want from your audience.
                </p>

                <p><strong>4. Poor Contrast and Accessibility</strong></p>
                <p>
                  Designers often prioritize "sleekness" over readability.
                </p>
                <p>
                  <strong>The Risk:</strong> Light grey text on a white background or
                  yellow text on green makes your brand inaccessible to people with
                  visual impairments and frustrates everyday users.
                </p>
              </div>

              <div className="image-box">
                <img src={img2} alt="Color and accessibility branding issues" />
              </div>
            </section>

            {/* ================= TYPOGRAPHY ================= */}
            <section className="blog-section">
              <h2>Typography & Consistency Issues</h2>

              <p><strong>5. Using Too Many Fonts</strong></p>
              <p>
                A major design mistake to avoid while branding is the "font buffet."
              </p>
              <p>
                <strong>The Risk:</strong> Using 4 or 5 different fonts across your
                website and social media makes your brand look chaotic and unorganized.
              </p>
              <p>
                <strong>The Fix:</strong> Stick to a maximum of two font families—one for
                headings and one for body text.
              </p>

              <p><strong>6. The "Inconsistency" Leak</strong></p>
              <p>
                Branding fails when the Instagram "vibe" doesn't match the website
                "vibe."
              </p>
              <p>
                <strong>The Risk:</strong> Inconsistency makes a brand look unreliable.
                If a user clicks an ad and lands on a site that feels like a different
                company, they will bounce.
              </p>
            </section>

            {/* ================= WIDE IMAGE ================= */}
            <div className="image-wide">
              <img src={img3} alt="Brand consistency and design system overview" />
            </div>

            {/* ================= AVOID MISTAKES ================= */}
            <section className="blog-section">
              <h2>How to Avoid Costly Branding Mistakes</h2>
              <p>
                To ensure your brand stands the test of time, follow these three rules:
              </p>
              <p>
                Create a Brand Style Guide: Document your rules so everyone on your team
                stays on the same page.
              </p>
              <p>
                Focus on the Audience, Not Yourself: You aren't the customer. Design for
                the people you are trying to serve.
              </p>
              <p>
                Invest Early: A cheap logo today often leads to a $10,000 rebrand
                tomorrow. Do it right the first time.
              </p>
            </section>

            {/* ================= FINAL ================= */}
            <section className="blog-section">
              <h2>The Bottom Line</h2>
              <p>
                The most expensive branding mistakes that hurt business are the ones you
                have to fix twice.
              </p>
              <p>
                By avoiding these design traps, you build a brand that is ready for
                takeoff.
              </p>
            </section>

          </div>
        </section>
      </PageTransition>
    </>
  );
}
